import { useGraphQLQuery } from '@opengov/bnp-core';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useRef } from 'react';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { getFragmentData, graphql } from '../../../../../gql-generated/hasura';
import {
  GetCustomReportsForTableQuery,
  Order_By,
  Spreadsheets_Bool_Exp,
  Spreadsheets_Order_By,
  SpreadsheetsProcessingStatus_Enum
} from '../../../../../gql-generated/hasura/graphql';
import { useToast } from '../../../Toast/context/useToast.context';
import locale from '../customReports-locale.json';
import { customReportsQueryKeys } from './customReportsQueryKeys';
import { getCompletedLocaleText } from '../../../../../locale/utils';

const UseFetchCustomReports_TrackingFragment = graphql(`
  fragment UseFetchCustomReports_TrackingFragment on spreadsheets {
    id
    name
    processingStatus
    spreadsheetNamedRanges_aggregate {
      aggregate {
        count
      }
    }
  }
`);

const FetchCustomReportsForTableQuery = graphql(`
  query GetCustomReportsForTable(
    $where: spreadsheets_bool_exp
    $orderBy: [spreadsheets_order_by!]
    $limit: Int
    $offset: Int
  ) {
    spreadsheets(where: $where, order_by: $orderBy, limit: $limit, offset: $offset) {
      ...UseFetchCustomReports_TrackingFragment
      ...CustomReportsTable_CustomReportFragment
    }
    spreadsheets_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export enum SortOption {
  NameAsc = 'name-asc',
  NameDesc = 'name-desc',
  CreatedMostRecent = 'created-most-recent',
  CreatedLeastRecent = 'created-least-recent',
  UpdatedMostRecent = 'updated-most-recent',
  UpdatedLeastRecent = 'updated-least-recent'
}

type UseFetchCustomReportsOptions = {
  publicationId: string;
  searchQuery: string;
  sortBy: SortOption;
  limit: number;
  offset: number;
};

const POLLING_INTERVAL_MS = 3000; // 3 seconds
const POLLING_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

function buildWhereClause(publicationId: string, searchQuery: string): Spreadsheets_Bool_Exp {
  const baseWhere: Spreadsheets_Bool_Exp = {
    publicationId: { _eq: publicationId }
  };

  const trimmedQuery = searchQuery.trim();
  if (trimmedQuery) {
    return {
      _and: [
        baseWhere,
        {
          name: { _ilike: `%${trimmedQuery}%` }
        }
      ]
    };
  }

  return baseWhere;
}

function buildOrderByClause(sortBy: SortOption): Spreadsheets_Order_By[] {
  switch (sortBy) {
    case SortOption.NameAsc:
      return [{ name: Order_By.Asc }];
    case SortOption.NameDesc:
      return [{ name: Order_By.Desc }];
    case SortOption.CreatedLeastRecent:
      return [{ createdAt: Order_By.Asc }];
    case SortOption.CreatedMostRecent:
      return [{ createdAt: Order_By.Desc }];
    case SortOption.UpdatedLeastRecent:
      return [{ updatedAt: Order_By.Asc }];
    case SortOption.UpdatedMostRecent:
      return [{ updatedAt: Order_By.Desc }];
    default: {
      const _exhaustiveCheck: never = sortBy;
      console.error('Unhandled sort option: ', _exhaustiveCheck);
      return [{ updatedAt: Order_By.Desc }];
    }
  }
}

export function useFetchCustomReports({
  publicationId,
  searchQuery,
  sortBy,
  limit,
  offset
}: UseFetchCustomReportsOptions) {
  const pollingStartTimeRef = useRef<number | null>(null);
  const hasShownTimeoutErrorRef = useRef(false);
  const previousStatusesRef = useRef<Map<string, SpreadsheetsProcessingStatus_Enum>>(new Map());
  const { toggleToast } = useToast();

  const result = useGraphQLQuery({
    queryKey: customReportsQueryKeys.customSpreadsheetReports({ publicationId, searchQuery, sortBy, limit, offset }),
    variables: {
      where: buildWhereClause(publicationId, searchQuery),
      orderBy: buildOrderByClause(sortBy),
      limit,
      offset
    },
    document: FetchCustomReportsForTableQuery,
    url: HASURA_SERVICE_URL,
    options: {
      select: (data: GetCustomReportsForTableQuery) => ({
        spreadsheets: data?.spreadsheets ?? [],
        totalCount: data?.spreadsheets_aggregate?.aggregate?.count ?? 0
      }),
      enabled: !isEmpty(publicationId),
      refetchInterval: (data) => {
        if (!data || !data.spreadsheets || data.spreadsheets.length === 0) {
          return false;
        }

        // Check if any report is still processing
        const hasIncompleteReports = data.spreadsheets.some((customReportFragment) => {
          const customReport = getFragmentData(UseFetchCustomReports_TrackingFragment, customReportFragment);
          return customReport?.processingStatus !== SpreadsheetsProcessingStatus_Enum.Completed;
        });

        if (!hasIncompleteReports) {
          // All reports completed, stop polling and reset
          pollingStartTimeRef.current = null;
          hasShownTimeoutErrorRef.current = false;
          return false;
        }

        // Start tracking polling time when we first detect incomplete reports
        if (pollingStartTimeRef.current === null) {
          pollingStartTimeRef.current = Date.now();
        }

        // Check if polling has exceeded 15 minutes
        const elapsedTime = Date.now() - pollingStartTimeRef.current;
        if (elapsedTime >= POLLING_TIMEOUT_MS) {
          // Show error toast only once
          if (!hasShownTimeoutErrorRef.current) {
            toggleToast({
              title: locale.errors.somethingWentWrong,
              body: locale.errors.processingTimeout,
              severity: 'error'
            });
            hasShownTimeoutErrorRef.current = true;
          }
          // Stop polling after timeout
          return false;
        }

        // Continue polling every 3 seconds
        return POLLING_INTERVAL_MS;
      },
      onSuccess: (data) => {
        if (!data || !data.spreadsheets || data.spreadsheets.length === 0) {
          return;
        }

        // Check if all reports are completed
        const hasIncompleteReports = data.spreadsheets.some((customReportFragment) => {
          const customReport = getFragmentData(UseFetchCustomReports_TrackingFragment, customReportFragment);
          return customReport?.processingStatus !== SpreadsheetsProcessingStatus_Enum.Completed;
        });

        // Detect transitions from in-progress to completed or failed
        data.spreadsheets.forEach((customReportFragment) => {
          const customReport = getFragmentData(UseFetchCustomReports_TrackingFragment, customReportFragment);
          if (!customReport) {
            return;
          }

          const previousStatus = previousStatusesRef.current.get(customReport.id);
          const currentStatus = customReport.processingStatus;

          // Check if status transitioned from in-progress (not Completed) to Completed
          if (
            previousStatus !== undefined &&
            previousStatus !== SpreadsheetsProcessingStatus_Enum.Completed &&
            currentStatus === SpreadsheetsProcessingStatus_Enum.Completed
          ) {
            const namedRangesCount = customReport.spreadsheetNamedRanges_aggregate.aggregate?.count ?? 0;
            const namedRangesText = namedRangesCount === 1 ? 'name range is' : 'name ranges are';

            toggleToast({
              title: locale.success.customReportUpdatedSuccessfully,
              body: getCompletedLocaleText(locale.success.customReportUpdatedSuccessfullyBody, {
                numNamedRanges: namedRangesCount,
                namedRangesText: namedRangesText
              }),
              severity: 'success'
            });
          }

          // Check if status transitioned from in-progress (not Failed) to Failed
          if (
            previousStatus !== undefined &&
            previousStatus !== SpreadsheetsProcessingStatus_Enum.Failed &&
            currentStatus === SpreadsheetsProcessingStatus_Enum.Failed
          ) {
            toggleToast({
              title: locale.errors.customReportProcessingFailed,
              body: locale.errors.customReportProcessingFailedBody,
              severity: 'error'
            });
          }

          // Update the previous status for this report
          previousStatusesRef.current.set(customReport.id, currentStatus);
        });

        // Clear previous statuses only after checking transitions and if all reports are completed
        if (!hasIncompleteReports) {
          previousStatusesRef.current.clear();
        }
      },
      onError: (error) => {
        console.error(error);
        toggleToast({
          title: locale.errors.somethingWentWrong,
          body: locale.errors.unableToFetchCustomReports,
          severity: 'error'
        });
      }
    }
  });

  // Reset polling timer when component unmounts
  useEffect(() => {
    const previousStatuses = previousStatusesRef.current;
    return () => {
      pollingStartTimeRef.current = null;
      hasShownTimeoutErrorRef.current = false;
      previousStatuses.clear();
    };
  }, []);

  return result;
}
